/**
 * Directions use the same numbering as vanilla so that update ordering,
 * which is observable in redstone circuits, can be matched exactly.
 */
export const Dir = {
  DOWN: 0,
  UP: 1,
  NORTH: 2,
  SOUTH: 3,
  WEST: 4,
  EAST: 5,
} as const;

export type Direction = 0 | 1 | 2 | 3 | 4 | 5;

export const DIR_NAMES = ['down', 'up', 'north', 'south', 'west', 'east'] as const;
export type DirName = (typeof DIR_NAMES)[number];

export const DIR_VEC: readonly (readonly [number, number, number])[] = [
  [0, -1, 0], // down
  [0, 1, 0], // up
  [0, 0, -1], // north
  [0, 0, 1], // south
  [-1, 0, 0], // west
  [1, 0, 0], // east
];

export const DIR_DX = Int8Array.from([0, 0, 0, 0, -1, 1]);
export const DIR_DY = Int8Array.from([-1, 1, 0, 0, 0, 0]);
export const DIR_DZ = Int8Array.from([0, 0, -1, 1, 0, 0]);

export const DIR_OPPOSITE = Uint8Array.from([1, 0, 3, 2, 5, 4]);

/** The four compass directions, in vanilla's horizontal order. */
export const HORIZONTALS: readonly Direction[] = [Dir.NORTH, Dir.SOUTH, Dir.WEST, Dir.EAST];
export const ALL_DIRS: readonly Direction[] = [
  Dir.DOWN,
  Dir.UP,
  Dir.NORTH,
  Dir.SOUTH,
  Dir.WEST,
  Dir.EAST,
];

/**
 * The order in which `updateNeighborsAt` visits neighbours in vanilla.
 * Redstone contraptions can distinguish this from any other ordering, so it is
 * reproduced exactly rather than using the natural 0..5 order.
 */
export const NEIGHBOR_UPDATE_ORDER: readonly Direction[] = [
  Dir.WEST,
  Dir.EAST,
  Dir.DOWN,
  Dir.UP,
  Dir.NORTH,
  Dir.SOUTH,
];

export function dirFromName(name: string): Direction {
  const i = DIR_NAMES.indexOf(name as DirName);
  if (i < 0) throw new Error(`unknown direction ${name}`);
  return i as Direction;
}

export function isHorizontal(d: Direction): boolean {
  return d >= 2;
}

/** Rotate a horizontal direction 90 degrees clockwise (viewed from above). */
export function rotateCW(d: Direction): Direction {
  switch (d) {
    case Dir.NORTH:
      return Dir.EAST;
    case Dir.EAST:
      return Dir.SOUTH;
    case Dir.SOUTH:
      return Dir.WEST;
    case Dir.WEST:
      return Dir.NORTH;
    default:
      return d;
  }
}

export function rotateCCW(d: Direction): Direction {
  return rotateCW(rotateCW(rotateCW(d)));
}

/** The horizontal direction a player at the given yaw (degrees) is facing. */
export function yawToDirection(yaw: number): Direction {
  const y = ((yaw % 360) + 360) % 360;
  if (y >= 315 || y < 45) return Dir.SOUTH;
  if (y < 135) return Dir.WEST;
  if (y < 225) return Dir.NORTH;
  return Dir.EAST;
}
